import { type DnDColumnItem, Flex, Text } from '@/components';
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToFirstScrollableAncestor,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import { arrayMove } from '@dnd-kit/sortable';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { SortableContainer } from './SortableContainer';

type Props = {
  columns: DnDColumnItem[];
  onRemove: (columnKey: string) => void;
  onChange: (columnKeys: string[]) => void;
  bodyMaxHeight?: number;
};

export const DnDSortableColumnSelection: FC<Props> = ({
  columns,
  onRemove,
  onChange,
  bodyMaxHeight,
}) => {
  const { t } = useTranslation();
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        // https://docs.dndkit.com/api-documentation/sensors/pointer#activation-constraints
        distance: 1,
      },
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over: target } = event;

    const getNewColumns = (prev: DnDColumnItem[]) => {
      const activeIndex = prev.findIndex((i) => i.title === active.id);
      const targetIndex = prev.findIndex((i) => i.title === target?.id);
      if (activeIndex < 0 || targetIndex < 0) {
        return prev;
      }
      return arrayMove(prev, activeIndex, targetIndex);
    };
    onChange(getNewColumns(columns).map((i) => i.key));
  };

  if (!columns.length) {
    return null;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis, restrictToFirstScrollableAncestor]}
    >
      <Flex gap={16} vertical>
        <Text
          variant="footnote"
          color="secondary"
          text={t('search-setting-displayed')}
        />
        <SortableContainer
          id="displayed"
          columns={columns}
          onHide={onRemove}
          maxHeight={bodyMaxHeight}
        />
      </Flex>
    </DndContext>
  );
};
